import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import React from 'react';
import type { FallbackProps } from 'react-error-boundary';

import { FontSizes, Radius, Spacing } from 'chatie-design-system';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';

import OopsIcon from '../../public/icons/oops.svg';
import OopsChemingIcon from '../../public/icons/oops_cheming.svg';
import { isChatieAPIError } from '../../utils/error';
import { OnClickProps } from '../../utils/misc';
import { useWebView } from '../../utils/webview';
import SvgIcon from './SvgIcon';

const errorCSS = (theme: Theme) => css`
  color: ${theme.var.gray400};
  text-align: center;
  width: 100%;

  h1 {
    font-size: 64px;
    margin-top: ${Spacing.medium}px;
  }

  p {
    margin-top: ${Spacing.medium}px;
  }

  > div {
    display: flex;
    margin-top: ${Spacing.medium}px;
    justify-content: center;
  }

  button {
    height: 46px;
    padding: 0 ${Spacing.medium}px;
    border-radius: ${Radius.medium}px;
    color: ${theme.true.white};
    font-size: ${FontSizes.small}px;
  }

  button:nth-of-type(1) {
    background-color: ${theme.colors.main500};
  }

  button:nth-of-type(2) {
    margin-left: ${Spacing.medium}px;
    background-color: ${theme.var.gray200};
  }
`;

type Props = {
  status?: number;
  text?: string;
};

export const ErrorFallback: React.FC<Props & OnClickProps<'onResetClick'>> = ({
  status,
  text,
  onResetClick,
}) => {
  const { platform, isCheming } = useGlobalContext();
  const { t } = useTranslation();
  const { web } = useWebView();
  const router = useRouter();

  const defaultAPIErrorMessages: { [key: number]: string } = {
    401: t('로그인 후 사용 가능합니다.'),
    403: t('권한이 없습니다.'),
    404: t('페이지를 찾을 수 없습니다.'),
  };

  const onClick = () => {
    if (platform === Platform.web) {
      router.push('/');
    }
    web.closePage();
  };

  return (
    <div css={errorCSS} className="center">
      <SvgIcon icon={isCheming ? OopsChemingIcon : OopsIcon} d={178} />
      {status && <h1>{status}</h1>}
      <p>
        {(status ? defaultAPIErrorMessages[status] : text) ??
          t('앗! 문제가 발생했습니다.')}
      </p>
      <div>
        {!status && <button onClick={onResetClick}>{t('재시도하기')}</button>}
        <button onClick={onClick}>{t('홈으로 가기')}</button>
      </div>
    </div>
  );
};

const ErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const { t } = useTranslation();

  if (error.message === 'Network Error') {
    return (
      <ErrorFallback
        text={t('인터넷 연결상태를 확인해주세요.')}
        onResetClick={resetErrorBoundary}
      />
    );
  }

  if (isChatieAPIError(error)) {
    return <ErrorFallback status={error.response.status} />;
  }

  return <ErrorFallback onResetClick={resetErrorBoundary} />;
};

export default ErrorBoundaryFallback;
